// utils/scopeAttachmentValidation.js
//
// Shared formatters and predicates for the opt-in attachment model.
//
// Three jobs:
//
//   1. Classify a backend guard refusal into a single `kind` so nine
//      screens can switch on one value instead of three (blocking_apps,
//      blocking_refs, error). See `classifyGuardRefusal`.
//
//   2. Classify a resource's scope from the caller's perspective, so
//      badges and dropdown groupings are computed identically across
//      every listing screen. See `describeScopeForApp`.
//
//   3. Format the "used by" data for inline chips and modal bodies.
//      See `formatAttachedAppChips`.
//
// This module has no React dependency. Keep it that way — it is meant
// to be imported by API helpers as well as by JSX screens.
//
// ───────────────────────────────────────────────────────────────────
// Guard refusals the backend can return
// ───────────────────────────────────────────────────────────────────
//
// 403 assert_attached (write blocked):
//     { error: "App 'X' has not opted in to tenant-scoped ... Attach it first via POST /scope_attach." }
//     No structured field. Detected by matching the message prefix.
//
// 409 guard_tenant_update / guard_tenant_delete (attached-in-use):
//     { success: false, error: "...", blocking_apps: ["a","b"], force_override_available: true|false }
//
// 409 reference guard (form/chain/leadgen still using the resource):
//     { success: false, error: "...", blocking_refs: { chains: [...], forms: [...] } }
//     (AutoBot) or { blocking_refs: { chains: [...], leadgen_forms: [...] } } (Connector)
//
// 400 _validate_scope_pair (caller passed both app_name and scope="tenant",
//     or neither, or an invalid scope value):
//     { error: "Missing 'scope'. Must be one of: app, tenant." }
//     or "scope='tenant' must not carry app_name ..." etc.
//
// 400 generic validation:
//     { error: "..." }
//
// `classifyGuardRefusal` maps all five into one of:
//   "attach-required"    — 403 assert_attached
//   "attached-in-use"    — 409 tenant-attachment guard (with blocking_apps)
//   "referenced"         — 409 reference guard (with blocking_refs)
//   "malformed-request"  — 400 scope-pair / invalid enum
//   "unknown"            — anything else (network, 500, etc.)

/**
 * Classify a backend guard refusal into a single `kind`.
 *
 * Callers use the `kind` to pick rendering — the Modal vs Inline banner
 * decision, the copy, and the "Force save anyway" affordance all hang
 * off the `kind`, not off inspecting the raw body.
 *
 * Returns:
 *   {
 *     kind:       "attach-required"
 *               | "attached-in-use"
 *               | "referenced"
 *               | "malformed-request"
 *               | "unknown",
 *     apps:       string[],       // populated for attached-in-use
 *     refs:       object | null,  // populated for referenced
 *     message:    string,         // human-readable, always present
 *     canForce:   boolean,        // true only for attached-in-use on update
 *     detail?:    string,         // raw server error, for logs
 *   }
 */
export function classifyGuardRefusal(body) {
  const empty = {
    kind: "unknown",
    apps: [],
    refs: null,
    message: "",
    canForce: false,
  };

  if (!body || typeof body !== "object") return empty;

  const rawError = typeof body.error === "string" ? body.error : "";
  const rawDetail = typeof body.detail === "string" ? body.detail : "";
  const rawMessage = typeof body.message === "string" ? body.message : "";
  const message = rawError || rawDetail || rawMessage;

  // ── 1. attach-required (403 from assert_attached) ──
  //
  // The backend message begins with "App '" and contains
  // "has not opted in to tenant-scoped". Match both substrings so
  // a coincidental "App '" in an unrelated error doesn't mis-classify.
  if (
    message.includes("has not opted in to tenant-scoped") &&
    message.includes("Attach it first")
  ) {
    return {
      ...empty,
      kind: "attach-required",
      message,
      detail: message,
    };
  }

  // ── 2. attached-in-use (409 with blocking_apps) ──
  if (Array.isArray(body.blocking_apps) && body.blocking_apps.length > 0) {
    return {
      kind: "attached-in-use",
      apps: body.blocking_apps.slice(),
      refs: null,
      message,
      canForce: body.force_override_available === true,
      detail: message,
    };
  }

  // ── 3. referenced (409 with blocking_refs) ──
  if (body.blocking_refs && typeof body.blocking_refs === "object") {
    return {
      kind: "referenced",
      apps: [],
      refs: body.blocking_refs,
      message,
      canForce: false,
      detail: message,
    };
  }

  // ── 4. malformed-request (400 from _validate_scope_pair and friends) ──
  //
  // The backend uses precise phrasing for these. Rather than matching
  // every variant, we match the three distinctive substrings the
  // _validate_scope_pair helper emits. Anything else falls to
  // "unknown" and gets rendered as a generic error.
  if (
    message.includes("Missing 'scope'") ||
    message.includes("Invalid scope") ||
    message.includes("scope='tenant' must not carry app_name") ||
    message.includes("scope='app' requires app_name")
  ) {
    return {
      ...empty,
      kind: "malformed-request",
      message,
      detail: message,
    };
  }

  // ── 5. unknown ──
  return {
    ...empty,
    message,
    detail: message,
  };
}

/**
 * Turn a list of attachment rows into chip-friendly app names, sorted
 * and deduped.
 *
 * Input shape: the `attachments` array from `listAttachments` /
 * `getAttachmentsByResource` — rows shaped like
 *   { app_name, attached_at, ... }
 *
 * Returns: [ { app_name, attached_at } ] sorted by app_name.
 *
 * Rows without an app_name are dropped. A caller that wants the full
 * rows (for a drill-down) should read the raw array — this helper
 * exists only for the chip stack.
 */
export function formatAttachedAppChips(attachments) {
  if (!Array.isArray(attachments)) return [];
  const seen = new Map();
  for (const row of attachments) {
    if (!row || typeof row !== "object") continue;
    const name = row.app_name;
    if (typeof name !== "string" || !name.trim()) continue;
    // Preserve the earliest attached_at we see per app.
    if (!seen.has(name)) {
      seen.set(name, {
        app_name: name,
        attached_at: row.attached_at || null,
      });
    } else {
      const prev = seen.get(name);
      if (
        row.attached_at &&
        (!prev.attached_at || row.attached_at < prev.attached_at)
      ) {
        prev.attached_at = row.attached_at;
      }
    }
  }
  return Array.from(seen.values()).sort((a, b) =>
    a.app_name.localeCompare(b.app_name),
  );
}

/**
 * Classify a resource's scope from the caller's perspective.
 *
 * Six states, versus the three from `classifyResourceScope`. The extra
 * three exist because a global admin needs to distinguish "attached by
 * me" (impossible — no app context) from "attached by others" (the
 * reason a delete is blocked).
 *
 * Returns one of:
 *   "app"                     — app-scoped row (app_name on the row)
 *   "tenant-unattached"       — tenant-scoped, zero apps attached
 *   "tenant-attached-by-me"   — tenant-scoped, this app attached it
 *                                (appName required)
 *   "tenant-attached-by-others" — tenant-scoped, one+ other apps
 *                                attached it; not this app
 *   "tenant-attached-by-many" — tenant-scoped, this app AND at least
 *                                one other app attached it
 *   "unknown"                 — scope field missing/invalid
 *
 * Params:
 *   resource          — the row (needs .scope and an id; the id field
 *                       name varies per resource — see the resolver
 *                       below)
 *   appName           — the current app, or undefined in global mode
 *   attachedAppNames  — Set<string> of app_names that have attached
 *                       this resource. Caller gets this from
 *                       getAttachmentsByResource(...).get(resourceId)
 *                       .app_names, or from a single-resource lookup.
 */
export function describeScopeForApp(
  resource,
  { appName, attachedAppNames } = {},
) {
  const scope = resource?.scope;
  if (scope === "app") return "app";
  if (scope !== "tenant") return "unknown";

  const ids = new Set(attachedAppNames instanceof Set ? attachedAppNames : []);
  const meAttached = !!(appName && ids.has(appName));
  const othersAttached = Array.from(ids).some((a) => a !== appName);

  if (meAttached && othersAttached) return "tenant-attached-by-many";
  if (meAttached) return "tenant-attached-by-me";
  if (othersAttached) return "tenant-attached-by-others";
  return "tenant-unattached";
}

/**
 * Classify a resource's scope from an app's perspective (three states).
 *
 * Retained because existing callers (APIConfigIndex, ChainApis) already
 * import this and expect three states. New code should prefer
 * `describeScopeForApp`, which carries more information.
 */
export function classifyResourceScope(resource, attachedIds) {
  const scope = resource?.scope;
  if (scope === "app") return "app";
  if (scope === "tenant") {
    const id =
      resource?.api_config_id ||
      resource?.chain_id ||
      resource?.definition_id ||
      resource?.component_id ||
      resource?.external_account_id ||
      resource?.api_config_id;
    if (id && attachedIds instanceof Set && attachedIds.has(id)) {
      return "tenant-attached";
    }
    return "tenant-available";
  }
  return "unknown";
}

/**
 * Render the "resource is in use" error as a consistent multi-line
 * string for `alert()` / ReusablePopup. Callers who want inline chips
 * should use `classifyGuardRefusal` + `formatAttachedAppChips` instead.
 *
 * Retained for backwards compatibility with existing call sites in
 * ConnectorDefinitionsPanel.jsx.
 */
export function formatResourceInUseError(body) {
  if (!body || typeof body !== "object") return "";

  const parts = [];

  const apps = Array.isArray(body.blocking_apps) ? body.blocking_apps : [];
  if (apps.length > 0) {
    parts.push(
      `This resource is attached to ${apps.length} app${apps.length === 1 ? "" : "s"
      }:\n` + apps.map((a) => `  • ${a}`).join("\n"),
    );
    if (body.force_override_available) {
      parts.push(
        "You can override this by ticking 'force' on the update form, " +
        "but the attached apps will see the change immediately.",
      );
    } else {
      parts.push(
        "Detach this resource from every app first, then try again.",
      );
    }
    return parts.join("\n\n");
  }

  if (body.blocking_refs && typeof body.blocking_refs === "object") {
    const chains = Array.isArray(body.blocking_refs.chains)
      ? body.blocking_refs.chains
      : [];
    const forms = Array.isArray(body.blocking_refs.forms)
      ? body.blocking_refs.forms
      : [];
    const leadgenForms = Array.isArray(body.blocking_refs.leadgen_forms)
      ? body.blocking_refs.leadgen_forms
      : [];

    if (chains.length > 0) {
      parts.push(
        `Used by ${chains.length} chain${chains.length === 1 ? "" : "s"}:\n` +
        chains
          .map((c) => {
            const label = c.name || c.chain_id || "(unnamed chain)";
            const where = c.where ? ` — ${c.where}` : "";
            return `  • ${label}${where}`;
          })
          .join("\n"),
      );
    }
    if (forms.length > 0) {
      parts.push(
        `Used by ${forms.length} form${forms.length === 1 ? "" : "s"}:\n` +
        forms
          .map((f) => {
            const label = f.form_uuid || "(unnamed form)";
            const app = f.app_name ? ` (app: ${f.app_name})` : "";
            return `  • ${label}${app}`;
          })
          .join("\n"),
      );
    }
    if (leadgenForms.length > 0) {
      parts.push(
        `Used by ${leadgenForms.length} leadgen form${leadgenForms.length === 1 ? "" : "s"
        }:\n` +
        leadgenForms
          .map((f) => {
            const label = f.form_name || f.form_uuid || "(unnamed form)";
            const app = f.app_name ? ` (app: ${f.app_name})` : "";
            return `  • ${label}${app}`;
          })
          .join("\n"),
      );
    }
    if (parts.length > 0) {
      parts.push(
        "Remove this resource from those chains and forms first, " +
        "or detach the chains that use it.",
      );
      return parts.join("\n\n");
    }
  }

  if (typeof body.error === "string" && body.error.trim()) {
    return body.error;
  }
  return "";
}

/**
 * Parse a JSON response body, tolerating non-JSON / empty bodies.
 * Returns {} on failure so callers can `await safeReadJson(resp)`
 * without a try/catch around every call site.
 */
export async function safeReadJson(resp) {
  try {
    return await resp.json();
  } catch {
    return {};
  }
}

/**
 * Predicate: is this body a guard refusal that the caller should
 * render as a warning (not a generic error)?
 */
export function isGuardRefusal(body) {
  if (!body || typeof body !== "object") return false;
  if (Array.isArray(body.blocking_apps)) return true;
  if (body.blocking_refs && typeof body.blocking_refs === "object") {
    return true;
  }
  return false;
}

/**
 * Human label for the scope badge. Consumed by ResourceScopeBadge.jsx.
 *
 * Keys must match the `kind` values accepted by ResourceScopeBadge and
 * produced by `describeScopeForApp` (plus "app" / "unknown" / the two
 * legacy `tenant-attached` / `tenant-available` from
 * `classifyResourceScope`).
 */
export const SCOPE_BADGE = {
  app: {
    label: "🏠 App",
    color: "#2f855a",
    bg: "#f0fff4",
    border: "#9ae6b4",
    title: "App-specific — only this app can use it",
  },
  "tenant-attached": {
    label: "🌐 Shared (attached)",
    color: "#2b6cb0",
    bg: "#ebf8ff",
    border: "#90cdf4",
    title:
      "Tenant-scoped and attached to this app — available in this app's dropdowns",
  },
  "tenant-available": {
    label: "🌐 Shared (available)",
    color: "#6b7280",
    bg: "#f9fafb",
    border: "#d1d5db",
    title:
      "Tenant-scoped but not attached to this app — click Attach to use it",
  },
  "tenant-detached-blocked": {
    label: "🔗 Attached by others",
    color: "#6b46c1",
    bg: "#f5f3ff",
    border: "#c4b5fd",
    title:
      "Tenant-scoped and attached by 1+ app(s). Click to inspect who, then detach before delete.",
  },
  "tenant-unattached": {
    label: "🌐 Unattached",
    color: "#6b7280",
    bg: "#f9fafb",
    border: "#d1d5db",
    title:
      "Tenant-scoped with no apps attached. Safe to delete.",
  },
  "tenant-attached-by-me": {
    label: "🌐 Shared (attached)",
    color: "#2b6cb0",
    bg: "#ebf8ff",
    border: "#90cdf4",
    title: "Tenant-scoped and attached to this app.",
  },
  "tenant-attached-by-others": {
    label: "🔗 Attached by others",
    color: "#6b46c1",
    bg: "#f5f3ff",
    border: "#c4b5fd",
    title:
      "Tenant-scoped and attached by one or more other apps. This app has not attached it.",
  },
  "tenant-attached-by-many": {
    label: "🌐 Shared (attached + others)",
    color: "#2b6cb0",
    bg: "#ebf8ff",
    border: "#90cdf4",
    title:
      "Tenant-scoped and attached by this app and at least one other.",
  },
  unknown: {
    label: "?",
    color: "#a0aec0",
    bg: "#f7fafc",
    border: "#e2e8f0",
    title: "Unknown scope",
  },
};

export default {
  classifyGuardRefusal,
  classifyResourceScope,
  describeScopeForApp,
  formatAttachedAppChips,
  formatResourceInUseError,
  isGuardRefusal,
  safeReadJson,
  SCOPE_BADGE,
};